<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { INSTRUCTION_TEXTS } from "@/utils/constant";
import StakingInfoPanel from "@/components/stake/StakingInfoPanel.vue";
import StakeTabs from "@/components/stake/StakeTabs.vue";
import StakeGrid from "@/components/stake/StakeGrid.vue";
import RewardsPanel from "@/components/stake/RewardsPanel.vue";
import TransferFromOldWalletDialog from "@/components/Dialog/TransferFromOldWalletDialog.vue";
// Import data mapper utility
import { mapNftToStandardFormat } from "@/utils/nfts/nftDataMapper";
// Import stores
import { useDojoStakingStore } from "@/plugins/2.pinia/modules/nfts/dojoStakingStore";
import { useStakedVrumblersStore } from "@/plugins/2.pinia/modules/nfts/stakedVrumblersStore";
import { useUnstakedVrumblersStore } from "@/plugins/2.pinia/modules/nfts/unstakedVrumblersStore";
import { useNftDialogStore } from "@/plugins/2.pinia/modules/nfts/nftDialogStore.js";
import { useAuthStore } from "@/plugins/2.pinia/modules/auth";
import { useDisplay } from "vuetify";
import { useSujiRewardsStore } from "@/plugins/2.pinia/modules/suji/sujiRewardsStore";

definePage({
  name: "StakeMobileMystics",
  path: "/stake/mobile-mystics",
  meta: {
    layout: "default",
    requiresAuth: true,
    title: "Stake Mystics",
    description: "Stake and manage mystic Vrumblers, rewards, and transfers.",
  },
});

// Store imports
const dojoStakingStore = useDojoStakingStore();
const stakedStore = useStakedVrumblersStore();
const unstakedVrumblersStore = useUnstakedVrumblersStore();
const onboarding = useOnboardingStore();
const authStore = useAuthStore();
const router = useRouter();
const { mobile } = useDisplay(); // ✨ NEW
const sujiRewardsStore = useSujiRewardsStore();
const nftDialogStore = useNftDialogStore();

// Reactive state
const activeTab = ref("MYSTICS"); // Default tab

const showTransferDialog = ref(false);

// Rewards data
const rewards = ref({
  $SUJI: 0,
  $NEXUS: 0,
});

// Connect to viewMode in dojoStakingStore
const stakingTab = computed({
  get: () => dojoStakingStore.viewMode,
  set: (value) => dojoStakingStore.setViewMode(value),
});

const normalizedStakedNfts = computed(() => {
  return stakedStore.stakedVrumblersArray?.map((nft) =>
    mapNftToStandardFormat(nft, true)
  );
});

// Active Vrumblers based on current view mode
const activeNfts = computed(() => {
  return stakingTab.value === "UNSTAKED"
    ? unstakedVrumblersStore.normalizedUnstakedVrumblers
    : normalizedStakedNfts.value;
});

// Computed property for overall loading state
const isLoadingAny = computed(() => {
  return (
    unstakedVrumblersStore.isLoading ||
    stakedStore.isLoading ||
    dojoStakingStore.isProcessing
  );
});

// Simplified computed properties
const unstakedCount = computed(
  () => unstakedVrumblersStore.unstakedVrumblersCount
);
const stakedCount = computed(() => normalizedStakedNfts.value?.length || 0);

// Handle staking tab change
const handleStakingTabChange = (tabId) => {
  stakingTab.value = tabId;
};

// OPEN THE TRANSFER DIALOG
const handleOpenTransferDialog = () => {
  console.log("Opening old wallet transfer dialog");
  showTransferDialog.value = true;
};

// TRANSFER COMPLETION
const handleTransferComplete = async (transferredTokenIds) => {
  console.log("Transfer completed with token IDs:", transferredTokenIds);

  console.log(
    `Successfully transferred ${transferredTokenIds.length} Vrumblers to your account`
  );

  // Refresh the NFT data to reflect the transfers
  await fetchNftData();
};

// Stake selected NFTs
const stakeSelectedNfts = async () => {
  if (dojoStakingStore.selectedTokenIdsToStake.length === 0) {
    console.warn("No NFTs selected for staking");
    return;
  }

  // Check wallet authorization before proceeding
  if (!dojoStakingStore.isWalletAuthorized()) {
    console.error("Wallet not authorized. Please reconnect your wallet.");
    dojoStakingStore.handleAuthorizationFailure();
    return;
  }

  try {
    // Store the count before staking (since it will be cleared after success)
    const countToStake = dojoStakingStore.selectedTokenIdsToStake.length;

    console.log(`Staking ${countToStake} NFTs...`);

    // Execute staking transaction using dojoStakingStore
    const result = await dojoStakingStore.stakeSelectedTokens();

    if (result) {
      // Use the stored count for the success message
      console.log(`Successfully staked ${countToStake} NFTs`);
      try {
        await sujiRewardsStore.refreshRewards();
      } catch (refreshError) {
        console.error("Failed to refresh $NEXUS rewards:", refreshError);
      }
    }
  } catch (err) {
    console.error("Staking error:", err);
  }
};

// Unstake selected NFTs
const unstakeSelectedNfts = async () => {
  if (dojoStakingStore.selectedTokenIdsToUnstake.length === 0) {
    console.warn("No NFTs selected for unstaking");
    return;
  }

  // Check wallet authorization before proceeding
  if (!dojoStakingStore.isWalletAuthorized()) {
    console.error("Wallet not authorized. Please reconnect your wallet.");
    dojoStakingStore.handleAuthorizationFailure();
    return;
  }

  try {
    // Store the count before unstaking (since it will be cleared after success)
    const countToUnstake = dojoStakingStore.selectedTokenIdsToUnstake.length;

    console.log(`Unstaking ${countToUnstake} NFTs...`);

    // Execute unstaking transaction using dojoStakingStore
    const result = await dojoStakingStore.unstakeSelectedTokens();

    if (result) {
      // Use the stored count for the success message
      console.log(`Successfully unstaked ${countToUnstake} NFTs`);
      try {
        await sujiRewardsStore.refreshRewards();
      } catch (refreshError) {
        console.error("Failed to refresh $NEXUS rewards:", refreshError);
      }
    }
  } catch (err) {
    console.error("Unstaking error:", err);
  }
};

// Handle NFT info display
const handleNftInfo = (itemId) => {
  if (!itemId) {
    console.error("Invalid NFT identifier for info dialog:", itemId);
    return;
  }

  nftDialogStore.openInventoryDialog({
    id: itemId,
    tab: "ALL",
  });
};

const openMarketplace = () => {
  nftDialogStore.openMarketDialog({ tab: "ALL" });
};

// Handle selection change from StakeGrid
const handleSelectionChange = (selection) => {
  console.log("Selection changed:", selection);

  // Update our selection in the dojoStakingStore
  if (selection.unstaked) {
    dojoStakingStore.clearStakeSelection();
    selection.unstaked.forEach((tokenId) => {
      dojoStakingStore.addToStakeSelection(tokenId);
    });
  }

  if (selection.staked) {
    dojoStakingStore.clearUnstakeSelection();
    selection.staked.forEach((tokenId) => {
      dojoStakingStore.addToUnstakeSelection(tokenId);
    });
  }
};

// Watch for store errors
watch(
  () => dojoStakingStore.stakingError,
  (error) => {
    if (error) {
      console.error("Staking error from store:", error);
    }
  }
);

watch(
  () => dojoStakingStore.unstakingError,
  (error) => {
    if (error) {
      console.error("Unstaking error from store:", error);
    }
  }
);

// Watch for auth connection status changes
watch(
  () => authStore.isConnectedPN,
  (isConnected) => {
    if (isConnected) {
      // Refresh data when reconnected
      fetchNftData();
    }
  }
);

// Fetch NFT data
const fetchNftData = async () => {
  try {
    await Promise.all([
      unstakedVrumblersStore.fetchUnstakedVrumblers(),
      stakedStore.fetchStakedVrumblers(),
    ]);

    console.log(
      `Loaded ${unstakedCount.value} unstaked NFTs and ${stakedCount.value} staked NFTs`
    );
  } catch (error) {
    console.error("Failed to load NFT data:", error);

    // Check if this is an auth error
    if (
      error.message?.includes("No wallet connected") ||
      error.message?.includes("Wallet authorization") ||
      error.message?.includes("Local Key not found")
    ) {
      dojoStakingStore.handleAuthorizationFailure();
    }
  }
};

// Initialize the stores and fetch data
onMounted(async () => {
  if (!mobile.value) {
    await router.push("/stake");
    return;
  }

  await nextTick();
  await fetchNftData();

  if (!onboarding.shown.stake) {
    onboarding.showInstruction({
      text: INSTRUCTION_TEXTS.stake,
      onClose: () => onboarding.markShown("stake"),
    });
  }
});

// Update stakingTabs labels with counts
const formattedStakingTabs = computed(() => {
  return [
    { id: "UNSTAKED", label: `UNSTAKED (${unstakedCount.value})` },
    { id: "STAKED", label: `STAKED (${stakedCount.value})` },
  ];
});
</script>

<template>
  <div>
    <!-- StarrySky as fixed background -->
    <StarrySky class="" />

    <div class="tw-page-container tw-min-h-screen tw-relative tw-z-10">
      <v-container>
        <div class="tw-flex tw-flex-col tw-gap-6">
          <my-profile-block />

          <loadout-block id="loadout-block" />

          <div class="tw-flex tw-flex-col tw-gap-4">
            <div class="tw-h-[50px]">
              <StakeTabs
                :activeTab="stakingTab"
                :tabs="formattedStakingTabs"
                variant="secondary"
                @change-tab="handleStakingTabChange"
              />
            </div>

            <div class="tw-rounded-lg tw-p-0 tw-flex-1">
              <StakeGrid
                :items="activeNfts"
                :activeTab="stakingTab"
                :loading="isLoadingAny"
                :mode="stakingTab === 'STAKED' ? 'unstake' : 'stake'"
                @item-info="handleNftInfo"
                @selection-change="handleSelectionChange"
              />

              <div
                class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mt-6 tw-mb-4"
              >
                <v-btn
                  v-if="stakingTab === 'UNSTAKED'"
                  color="transparent"
                  flat
                  rounded="xl"
                  variant="elevated"
                  :loading="dojoStakingStore.isStaking"
                  :disabled="!dojoStakingStore.hasStakeSelection"
                  @click="stakeSelectedNfts"
                  class="tw-px-8"
                >
                  {{ dojoStakingStore.hasStakeSelection ? "Stake" : "Select" }}
                  {{
                    dojoStakingStore.hasStakeSelection
                      ? dojoStakingStore.selectedTokenIdsToStake.length
                      : ""
                  }}
                  {{
                    dojoStakingStore.hasStakeSelection
                      ? dojoStakingStore.selectedTokenIdsToStake.length !== 1
                        ? "NFTs"
                        : "NFT"
                      : "NFTs"
                  }}
                </v-btn>

                <v-btn
                  v-if="stakingTab === 'STAKED'"
                  color="transparent"
                  flat
                  rounded="xl"
                  variant="elevated"
                  :loading="dojoStakingStore.isUnstaking"
                  :disabled="!dojoStakingStore.hasUnstakeSelection"
                  @click="unstakeSelectedNfts"
                  class="tw-px-8"
                >
                  {{
                    dojoStakingStore.hasUnstakeSelection ? "Unstake" : "Select"
                  }}
                  {{
                    dojoStakingStore.hasUnstakeSelection
                      ? dojoStakingStore.selectedTokenIdsToUnstake.length
                      : ""
                  }}
                  {{
                    dojoStakingStore.hasUnstakeSelection
                      ? dojoStakingStore.selectedTokenIdsToUnstake.length !== 1
                        ? "NFTs"
                        : "NFT"
                      : "NFTs"
                  }}
                </v-btn>

                <v-btn
                  color="primary"
                  rounded="pill"
                  variant="flat"
                  class="tw-font-semibold tw-text-xs tw-tracking-wide tw-px-6 tw-text-slate-900 tw-bg-gradient-to-r tw-from-amber-400 tw-via-orange-500 tw-to-rose-500 tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                  @click="openMarketplace"
                >
                  <v-icon
                    icon="mdi-storefront-outline"
                    size="18"
                    class="tw-mr-2"
                  />
                  Open Marketplace
                </v-btn>
              </div>
            </div>
          </div>

          <RewardsPanel
            :activeTab="activeTab"
            :rewards="rewards"
            :stakedMysticsCount="stakedCount"
          />

          <StakingInfoPanel
            @open-old-wallet-dialog="handleOpenTransferDialog"
          />
        </div>
      </v-container>
    </div>

    <!-- OLD WALLET TRANSFER DIALOG -->
    <TransferFromOldWalletDialog
      v-model="showTransferDialog"
      @transfer-complete="handleTransferComplete"
    />
  </div>
</template>
